import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { errMsg } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Logo, Wordmark } from "../../components/shared/Logo";
import { Loader2 } from "lucide-react";

export const AuthShell = ({ title, subtitle, children }) => (
  <div className="min-h-screen bg-background flex">
    <div className="hidden lg:flex w-[45%] bg-[#0A0A0C] text-white flex-col justify-between p-12 relative overflow-hidden">
      <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(ellipse at 20% 80%, rgba(0,122,255,0.25), transparent 55%)" }} />
      <Link to="/" className="flex items-center gap-2.5 relative z-10"><Logo size={28} /><Wordmark variant="silver" /></Link>
      <div className="relative z-10">
        <p className="overline-cc mb-4">Command Interface</p>
        <h1 className="font-heading text-4xl font-bold leading-tight tracking-tight">Precision operations.<br />Executive control.</h1>
        <p className="mt-4 text-white/50 text-sm max-w-sm leading-relaxed">One platform for quotes, missions, crews, invoices, and payments — engineered for people who value execution.</p>
      </div>
      <p className="text-xs text-white/30 font-mono-cc relative z-10">SAN DIEGO · OCEANSIDE · LA · VEGAS</p>
    </div>
    <div className="flex-1 flex items-center justify-center p-6">
      <div className="w-full max-w-sm fade-up">
        <Link to="/" className="lg:hidden flex items-center gap-2 mb-8"><Logo size={24} /><Wordmark /></Link>
        <h2 className="font-heading text-2xl font-bold">{title}</h2>
        <p className="text-sm text-muted-foreground mt-1 mb-8">{subtitle}</p>
        {children}
      </div>
    </div>
  </div>
);

export const GoogleButton = ({ onClick }) => (
  <Button data-testid="google-login-button" type="button" variant="outline" className="w-full rounded-full h-11" onClick={onClick}>
    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18A10.96 10.96 0 0 0 1 12c0 1.77.43 3.45 1.18 4.94l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/></svg>
    Continue with Google
  </Button>
);

export default function Login() {
  const { login, googleLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password, remember);
      navigate(user.role === "crew" ? "/crew" : user.role === "customer" ? "/portal" : "/ops");
    } catch (err) {
      setError(errMsg(err));
    }
    setLoading(false);
  };

  return (
    <AuthShell title="Sign in" subtitle="Access your portal or CastleOps.">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" name="email" autoComplete="email" data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input id="password" name="password" autoComplete="current-password" data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
        </div>
        <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid="login-remember-row">
          <Checkbox id="remember" data-testid="login-remember-checkbox" checked={remember} onCheckedChange={(v) => setRemember(!!v)} />
          <span className="text-sm text-muted-foreground">Keep me signed in</span>
        </label>
        {error && <p data-testid="login-error-message" className="text-sm text-red-500">{error}</p>}
        <Button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full rounded-full h-11 btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-semibold">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
        </Button>
      </form>
      <div className="flex items-center gap-3 my-6">
        <div className="flex-1 h-px bg-border" /><span className="text-xs text-muted-foreground">OR</span><div className="flex-1 h-px bg-border" />
      </div>
      <GoogleButton onClick={googleLogin} />
      <p className="text-sm text-muted-foreground mt-8 text-center">
        No account? <Link to="/register" data-testid="login-register-link" className="text-[var(--cc-accent)] font-semibold">Create one</Link>
      </p>
    </AuthShell>
  );
}
